// The display language of the Machine surface, in one place.
//
// Meteoric drew this cockpit in its own palette: green for a healthy stage,
// amber for a warning, red for a problem. This portal's brand layer has no green
// and no amber, and its one rule about red is explicit — danger and nothing
// else (`src/styles/global.css`). So a stage, a status and a tier are carried by
// their written NAME plus a badge weight, exactly as the three sibling modules
// carry a band.
//
// That is also the more legible encoding across a surface this size: thirteen
// tabs of coloured chips is a surface nobody can scan, and the words survive a
// colourblind reader, a greyscale print, and a screenshot pasted into Slack.

import type { ObjectiveBand, StatusChipTone, StageTone } from "./data/cockpit-data";

export type Tone = "critical" | "watch" | "ok" | "neutral";

/** The shadcn `Badge` variant for a tone. `outline` carries watch and neutral. */
export function badgeVariant(tone: Tone): "default" | "secondary" | "destructive" | "outline" {
  if (tone === "critical") return "destructive";
  if (tone === "ok") return "secondary";
  return "outline";
}

/**
 * A proposal stage's tone.
 *
 * `protect` is the only stage that reads as a problem — it is the negative
 * contribution-profit stage, where the machine is stopping a loss. Meteoric drew
 * it amber; here it is the one stage allowed weight, and it is still named on
 * screen rather than left to the badge alone.
 */
export function stageTone(tone: StageTone): Tone {
  if (tone === "protect") return "critical";
  if (tone === "capture") return "ok";
  if (tone === "config") return "neutral";
  return "watch";
}

export function stageBadgeVariant(tone: StageTone): ReturnType<typeof badgeVariant> {
  return badgeVariant(stageTone(tone));
}

/**
 * A status chip's tone.
 *
 * Meteoric had five: neutral, ok, warn, bad, purple — a green tick, an amber
 * bang, a red cross and a purple dot. Four of the five collapse here, because
 * this brand layer has three weights and one of them is reserved for danger.
 *
 * The information does NOT collapse with them: `statusChip` still returns the
 * word ("EXECUTING", "DEFERRED", "FAILED"), and the word is what the badge
 * shows. A reader who could previously tell approved from executed by colour
 * can still tell them apart by reading them — and can now also do it in
 * greyscale, colourblind, or in a screenshot.
 */
export function statusTone(tone: StatusChipTone): Tone {
  if (tone === "bad") return "critical";
  if (tone === "ok") return "ok";
  if (tone === "neutral") return "neutral";
  // `warn` and `purple` both become outline. Reserving red for danger means
  // "deferred" and "proposed" are carried by their names, which is what a
  // reader says out loud anyway.
  return "watch";
}

export function statusBadgeVariant(tone: StatusChipTone): ReturnType<typeof badgeVariant> {
  return badgeVariant(statusTone(tone));
}

/**
 * An objective's profitability band.
 *
 * Meteoric tinted the whole row green / amber / red and put a coloured bar down
 * its left edge. Neither survives: a tinted table row is the least legible
 * encoding on the surface (it competes with selection and hover, and it is
 * invisible in greyscale), and `unknown` rendered as "no tint" was
 * indistinguishable from a row whose band simply had not loaded.
 *
 * The band is now a named badge in its own column-adjacent position, so
 * "unknown" is a word a reader can see rather than an absence they must infer.
 */
export function objectiveBandTone(band: ObjectiveBand): Tone {
  if (band === "red") return "critical";
  if (band === "green") return "ok";
  if (band === "amber") return "watch";
  return "neutral";
}

export const OBJECTIVE_BAND_LABEL: Record<ObjectiveBand, string> = {
  green: "On plan",
  amber: "Watch",
  red: "Losing money",
  unknown: "Band unknown",
};

/**
 * The profit-yield ratio bands used by the Stats and Keyword Status tables.
 *
 * The thresholds are Meteoric's, unchanged (1.5 / 1.2 / 0.9). What changes is
 * that the number is no longer painted: the badge weights it and the `title`
 * names the band, so a reader can say which band a figure is in rather than
 * having to remember what a particular shade of green meant.
 */
export function ratioBandTone(value: number): Tone {
  if (value >= 1.2) return "ok";
  if (value >= 0.9) return "watch";
  return "critical";
}

export function ratioBandLabel(value: number): string {
  if (value >= 1.5) return "Outstanding";
  if (value >= 1.2) return "Excellent";
  if (value >= 0.9) return "At risk";
  return "Critical";
}
